import { lazy, Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider } from './contexts/AuthContext';
import { ClientAuthProvider } from './contexts/ClientAuthContext';
import { ToastProvider } from './contexts/ToastContext';
import ErrorBoundary from './components/common/ErrorBoundary';
import ToastContainer from './components/common/ToastContainer';
import LoadingSpinner from './components/common/LoadingSpinner';

// Layouty są malutkie i potrzebne natychmiast po starcie — ładujemy synchronicznie.
import AdminLayout from './components/layout/AdminLayout';
import PortalLayout from './components/layout/PortalLayout';

// Public-auth pages — krytyczne dla pierwszego ekranu (login/rejestracja). Sync.
import Login from './pages/Login';
import PortalLogin from './pages/portal/PortalLogin';
import Register from './pages/portal/Register';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';

// Wszystkie inne strony — lazy-loaded (osobne chunki, ładowane na żądanie).
// Każda strona admina/portalu trafia tylko gdy user na nią wejdzie → mniejszy
// initial bundle, szybsze pierwsze wyrenderowanie.
const Dashboard = lazy(() => import('./pages/Dashboard'));
const Plans = lazy(() => import('./pages/Plans'));
const ApiKeys = lazy(() => import('./pages/ApiKeys'));
const Clients = lazy(() => import('./pages/Clients'));
const Map = lazy(() => import('./pages/Map'));
const ActivationCodes = lazy(() => import('./pages/ActivationCodes'));
const GraviteeImport = lazy(() => import('./pages/GraviteeImport'));
const Admins = lazy(() => import('./pages/Admins'));
const AuditLogs = lazy(() => import('./pages/AuditLogs'));
const ModelAccuracy = lazy(() => import('./pages/ModelAccuracy'));
const AdminTokens = lazy(() => import('./pages/AdminTokens'));
const AuthHeaders = lazy(() => import('./pages/AuthHeaders'));

const PortalDashboard = lazy(() => import('./pages/portal/Dashboard'));
const ClientSettings = lazy(() => import('./pages/ClientSettings'));
const ClientUsage = lazy(() => import('./pages/ClientUsage'));
const PortalCorrection = lazy(() => import('./pages/portal/Correction'));
const PortalHelp = lazy(() => import('./pages/portal/Help'));
const PortalWebhooks = lazy(() => import('./pages/portal/Webhooks'));
const PortalAlerts = lazy(() => import('./pages/portal/Alerts'));

function PageFallback() {
  return (
    <div className="flex items-center justify-center min-h-[60vh]">
      <LoadingSpinner size="lg" />
    </div>
  );
}

function App() {
  return (
    <ErrorBoundary>
      <ToastProvider>
        <Router>
          <AuthProvider>
            <ClientAuthProvider>
              <ToastContainer />
              <Suspense fallback={<PageFallback />}>
                <Routes>
                  {/* Redirect root to portal login */}
                  <Route path="/" element={<Navigate to="/portal/login" replace />} />

                  {/* Admin Login (public) */}
                  <Route path="/admin/login" element={<Login />} />

                  {/* Admin Panel (protected) */}
                  <Route path="/admin" element={<AdminLayout />}>
                    <Route index element={<Navigate to="/admin/dashboard" replace />} />
                    <Route path="dashboard" element={<Dashboard />} />
                    <Route path="plans" element={<Plans />} />
                    <Route path="api-keys" element={<ApiKeys />} />
                    <Route path="clients" element={<Clients />} />
                    <Route path="map" element={<Map />} />
                    <Route path="activation-codes" element={<ActivationCodes />} />
                    <Route path="gravitee-import" element={<GraviteeImport />} />
                    <Route path="admins" element={<Admins />} />
                    <Route path="audit-logs" element={<AuditLogs />} />
                    <Route path="model-accuracy" element={<ModelAccuracy />} />
                    <Route path="tokens" element={<AdminTokens />} />
                    <Route path="auth-headers" element={<AuthHeaders />} />
                  </Route>

                  {/* Client Portal - Public Routes */}
                  <Route path="/portal/register" element={<Register />} />
                  <Route path="/portal/login" element={<PortalLogin />} />
                  <Route path="/portal/forgot-password" element={<ForgotPassword />} />
                  <Route path="/portal/reset-password" element={<ResetPassword />} />

                  {/* Client Portal - Protected Routes */}
                  <Route path="/portal" element={<PortalLayout />}>
                    <Route index element={<Navigate to="/portal/dashboard" replace />} />
                    <Route path="dashboard" element={<PortalDashboard />} />
                    <Route path="usage" element={<ClientUsage />} />
                    <Route path="correction" element={<PortalCorrection />} />
                    <Route path="help" element={<PortalHelp />} />
                    <Route path="webhooks" element={<PortalWebhooks />} />
                    <Route path="alerts" element={<PortalAlerts />} />
                    <Route path="settings" element={<ClientSettings />} />
                  </Route>

                  {/* 404 */}
                  <Route path="*" element={<Navigate to="/portal/login" replace />} />
                </Routes>
              </Suspense>
            </ClientAuthProvider>
          </AuthProvider>
        </Router>
      </ToastProvider>
    </ErrorBoundary>
  );
}

export default App;
